import {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  Children,
  cloneElement
} from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import FocusTrap from 'focus-trap-react'

import { features } from '../../media'
import { PB, hexToHsl } from '../../colors'

const [hue, sat, light] = hexToHsl(PB)
const getCentering = ({ centered }) => {
  if (centered) {
    return `
      display: flex;
      flex-direction: column;
      justify-content: center;
    `
  }
}
const Overlay = styled.div`
  position: fixed;
  z-index: 10000;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: hsla(${hue}, ${sat}%, ${light}%, 0.5);
  overflow-x: hidden;
  overflow-y: auto;
  ${getCentering};
`
const Content = styled.div`
  font-family: proxima-nova, 'Proxima Nova', sans-serif;
  width: auto;
  margin: 15px;
  background: #fff;
  border: ${({ hasBorders }) => (hasBorders ? '4px solid black' : 'none')};
  border-radius: ${({ hasBorders }) => (hasBorders ? 0 : '5px')};
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
  box-sizing: border-box;
  padding: ${({ isPadded }) => (isPadded ? '30px' : 0)};

  * {
    box-sizing: border-box;
  }

  @media (${features.minWidth.laptop}) {
    width: 768px;
    margin: 30px auto;
  }
  @media (${features.minWidth.desktop}) {
    width: 900px;
  }
  @media (${features.minWidth.widescreen}) {
    width: 1000px;
  }
`
const HeaderWrap = styled.div`
  position: relative;
  margin-bottom: 15px;
  padding-top: 10px;
`
const Close = styled(FontAwesomeIcon)`
  font-family: proxima-nova, 'Proxima Nova', sans-serif;
  font-size: 26px;
  color: black;
`
const CloseButton = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  position: absolute;
  right: -10px;
  top: -15px;
  cursor: pointer;
`
const BodyWrap = styled.div`
  p > strong {
    font-size: 30px;
    color: ${PB};
  }
`
const FooterWrap = styled.div`
  margin-top: 15px;
`
const ModalHeader = ({ children, hasCloseButton = true, ...rest }) => {
  let foundHeader = false

  return (
    <HeaderWrap>
      {Children.map(children, child => {
        if (child.props?.level === '2' && !foundHeader) {
          foundHeader = true

          return cloneElement(child, { id: 'modal-title' })
        }

        return child
      })}
      {hasCloseButton && (
        <CloseButton
          aria-label="close"
          onClick={evt => {
            rest.setOpen(false)
            rest.onClose(evt)
          }}>
          <Close icon="times" />
        </CloseButton>
      )}
    </HeaderWrap>
  )
}
const ModalBody = ({ children }) => <BodyWrap>{children}</BodyWrap>
const ModalFooter = ({ children }) => <FooterWrap>{children}</FooterWrap>
/**
 * The Modal component renders content in front of a backdrop and can be used for dialogs, popovers, notifications, etc.
 */
const Modal = ({
  children,
  isOpen = false,
  isPadded = true,
  closeOnClickOverlay = true,
  hasBorders = false,
  onClose = () => {}
}) => {
  const overlayRef = useRef()
  const contentRef = useRef()
  const [trapFocus, setTrapFocus] = useState(true)
  const [open, setOpen] = useState(isOpen)
  const [centered, setCentered] = useState(false)
  const [bodyOverflow, setBodyOverflow] = useState('visible')
  const handleOverlayClick = useCallback(
    evt => {
      if (closeOnClickOverlay) {
        setOpen(false)
        onClose(evt)
      }
    },
    [closeOnClickOverlay, onClose]
  )
  const handleClickContent = useCallback(evt => evt.stopPropagation(), [])

  useEffect(() => {
    const onFocusIn = () => {
      /*
       * .tox-dialog is the TINY MCE dialog that opens separately for links, graphics, etc..., it should not
       * trap focus in this modal if it is open since the two modals compete for focus
       */
      if (document.querySelectorAll('.tox-dialog').length > 0) {
        setTrapFocus(false)
      } else if (trapFocus) {
        setTrapFocus(true)
      }
    }

    window.addEventListener('focusin', onFocusIn)

    return () => {
      window.removeEventListener('focusin', onFocusIn)
    }
  }, [trapFocus])

  useEffect(() => {
    setBodyOverflow(document.body.style.overflow)
  }, [])

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : bodyOverflow

    setOpen(isOpen)

    return () => (document.body.style.overflow = bodyOverflow)
  }, [bodyOverflow, isOpen])

  useEffect(() => {
    const onKeyUp = evt => {
      if (evt.key === 'Escape') {
        setOpen(false)
        onClose(evt)
      }
    }

    document.addEventListener('keyup', onKeyUp)

    return () => document.removeEventListener('keyup', onKeyUp)
  }, [onClose])

  useLayoutEffect(() => {
    const overlayHeight = overlayRef.current?.offsetHeight
    const contentHeight = contentRef.current?.offsetHeight
    const diff = overlayHeight - contentHeight

    if (open && diff > 0) {
      setCentered(true)
    } else {
      setCentered(false)
    }
  }, [open, contentRef.current?.offsetHeight])

  if (!open) {
    return null
  }

  return createPortal(
    <Overlay
      role="dialog"
      ref={overlayRef}
      aria-modal={true}
      aria-labelledby={'modal-title'}
      onClick={handleOverlayClick}
      centered={centered}>
      <FocusTrap active={trapFocus}>
        <Content
          ref={contentRef}
          onClick={handleClickContent}
          hasBorders={hasBorders}
          isPadded={isPadded}>
          {Children.map(children, child => {
            if (child.type.slotName === ModalHeader.slotName) {
              return cloneElement(child, { onClose, setOpen })
            }

            return child
          })}
        </Content>
      </FocusTrap>
    </Overlay>,
    document.body
  )
}

Modal.propTypes = {
  /** Whether the modal is open or not. */
  isOpen: PropTypes.bool,
  /** Whether the modal has default padding applied */
  isPadded: PropTypes.bool,
  /** Close the modal when clicking the overlay backdrop. */
  closeOnClickOverlay: PropTypes.bool,
  /** Callback when closing the modal. */
  onClose: PropTypes.func,
  /** Whether the outer Content has borders. */
  hasBorders: PropTypes.bool,
  /** Modal children, any combination of Modal.Header, Modal.Body, and Modal.Footer. */
  children: PropTypes.oneOfType([
    PropTypes.shape({
      type: PropTypes.oneOf([ModalHeader, ModalBody, ModalFooter])
    }),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.shape({
          type: PropTypes.oneOf([ModalHeader])
        }),
        PropTypes.shape({
          type: PropTypes.oneOf([ModalBody])
        }),
        PropTypes.shape({
          type: PropTypes.oneOf([ModalFooter])
        })
      ])
    )
  ]).isRequired
}

ModalHeader.slotName = 'ModalHeader'
ModalHeader.propTypes = {
  hasCloseButton: PropTypes.bool,
  children: PropTypes.node
}
ModalBody.slotName = 'ModalBody'
ModalBody.propTypes = {
  children: PropTypes.node.isRequired
}
ModalFooter.slotName = 'ModalFooter'
ModalFooter.propTypes = {
  children: PropTypes.node.isRequired
}
Modal.Header = ModalHeader
Modal.Body = ModalBody
Modal.Footer = ModalFooter

export { Modal }
